import React, { useMemo, useState, useCallback, useRef } from "react";
import { Plus, Trash2, CheckCircle, AlertTriangle, Clock, ChevronDown, ChevronUp, Edit2, Copy, FileDown, FileUp, Loader2 } from "lucide-react";
import { importF24FromPdf } from "../services/f24ImportService";
import TopBar from "../components/layout/TopBar";
import F24Modal from "../components/f24/F24Modal";
import UpgradePrompt from "../components/common/UpgradePrompt";
import { useAppState } from "../hooks/useAppState";
import { useSubscription } from "../hooks/useSubscription";
import { calcolaRavvedimento, formatEuro } from "../utils/ravvedimentoCalc";
import { exportF24List } from "../services/exportService";

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateIT(isoDate) {
  if (!isoDate) return "—";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}/${month}/${year}`;
}

function getDaysUntil(dateStr) {
  const today = new Date(todayISO());
  const target = new Date(dateStr);
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
}

// Sposta l'anno di una data ISO (YYYY-MM-DD) mantenendo mese e giorno.
function shiftYearISO(isoDate, targetYear) {
  if (!isoDate) return "";
  const parts = isoDate.split("-");
  if (parts.length !== 3) return isoDate;
  parts[0] = String(targetYear);
  return parts.join("-");
}

const FILTER_STATI = [
  { value: "tutti", label: "Tutti" },
  { value: "da_pagare", label: "Da pagare" },
  { value: "pagati", label: "Pagati" },
  { value: "scaduti", label: "Scaduti" },
];

export default function F24Page() {
  const {
    darkMode,
    toggleDarkMode,
    user,
    selectedYear,
    setSelectedYear,
    f24ForYear,
    createF24,
    updateF24,
    deleteF24,
    markF24AsPaid,
    fiscalSettings,
    isSynced,
  } = useAppState();

  const { loading: subLoading, canAccessFeature, isPro } = useSubscription();
  const f24Locked = !subLoading && !canAccessFeature('f24');

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingF24, setEditingF24] = useState(null);
  const [filtroStato, setFiltroStato] = useState("tutti");
  const [expandedRavv, setExpandedRavv] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [showExportUpgrade, setShowExportUpgrade] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const pdfInputRef = useRef(null);

  const today = todayISO();

  // Classifica F24
  const { scaduti, inScadenza, pagati } = useMemo(() => {
    const s = [], i = [], p = [];
    for (const f of f24ForYear) {
      if (f.dataPagamento) {
        p.push(f);
      } else if (f.dataScadenza < today) {
        s.push(f);
      } else {
        i.push(f);
      }
    }
    s.sort((a, b) => a.dataScadenza.localeCompare(b.dataScadenza));
    i.sort((a, b) => a.dataScadenza.localeCompare(b.dataScadenza));
    p.sort((a, b) => (b.dataPagamento || "").localeCompare(a.dataPagamento || ""));
    return { scaduti: s, inScadenza: i, pagati: p };
  }, [f24ForYear, today]);

  // Filtro
  const filtered = useMemo(() => {
    if (filtroStato === "tutti") return { scaduti, inScadenza, pagati };
    if (filtroStato === "scaduti") return { scaduti, inScadenza: [], pagati: [] };
    if (filtroStato === "da_pagare") return { scaduti: [], inScadenza, pagati: [] };
    if (filtroStato === "pagati") return { scaduti: [], inScadenza: [], pagati };
    return { scaduti, inScadenza, pagati };
  }, [filtroStato, scaduti, inScadenza, pagati]);

  // Riepilogo
  const riepilogo = useMemo(() => {
    const totaleDaPagare = [...scaduti, ...inScadenza].reduce((sum, f) => sum + f.importo, 0);
    const totaleScaduti = scaduti.reduce((sum, f) => sum + f.importo, 0);
    const totaleConRavvedimento = scaduti.reduce((sum, f) => {
      const ravv = calcolaRavvedimento(f.importo, f.dataScadenza, today);
      return sum + ravv.totale;
    }, 0);
    const prossimo = inScadenza[0] || null;
    return { totaleDaPagare, totaleScaduti, totaleConRavvedimento, prossimo };
  }, [scaduti, inScadenza, today]);

  const handleCreate = () => {
    setModalMode("create");
    setEditingF24(null);
    setModalOpen(true);
  };

  // Import da PDF: Claude estrae i dati (Cloud Function parseF24Pdf) e il
  // risultato apre il modale precompilato, così l'utente può correggerlo prima di salvare.
  const handlePickPdf = () => {
    setImportError(null);
    if (pdfInputRef.current) pdfInputRef.current.click();
  };

  const handlePdfSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // consente di riselezionare lo stesso file
    if (!file) return;

    setImportError(null);
    setImporting(true);
    try {
      const draft = await importF24FromPdf(file);
      setModalMode("create");
      setEditingF24(draft);
      setModalOpen(true);
    } catch (err) {
      setImportError(err?.message || "Impossibile leggere il PDF");
    } finally {
      setImporting(false);
    }
  };

  const handleEdit = (f24) => {
    setModalMode("edit");
    setEditingF24(f24);
    setModalOpen(true);
  };

  const handleDuplicate = (f24) => {
    const targetYear = new Date().getFullYear();
    const oldYear = Number(f24.annoRiferimento) || Number(f24.anno) || null;
    const draft = {
      categoria: f24.categoria,
      descrizione: oldYear
        ? (f24.descrizione || "").replaceAll(String(oldYear), String(targetYear))
        : (f24.descrizione || ""),
      dataScadenza: shiftYearISO(f24.dataScadenza, targetYear),
      annoRiferimento: targetYear,
      anno: targetYear,
      note: f24.note || "",
      righe: Array.isArray(f24.righe) ? f24.righe.map((r) => ({ ...r })) : [],
    };
    setModalMode("duplicate");
    setEditingF24(draft);
    setModalOpen(true);
  };

  const handleSubmit = async (data) => {
    if (modalMode === "edit" && editingF24) {
      await updateF24(editingF24.id, data);
    } else {
      await createF24(data);
      // Dopo una duplicazione passa all'anno di destinazione così il nuovo F24 è visibile.
      if (modalMode === "duplicate" && data.annoRiferimento) {
        setSelectedYear(Number(data.annoRiferimento));
      }
    }
  };

  const handleMarkPaid = async (f24) => {
    setPayingId(f24.id);
    await markF24AsPaid(f24.id, todayISO());
    setPayingId(null);
  };

  const handleDelete = async (id) => {
    await deleteF24(id);
    setConfirmDelete(null);
  };

  const toggleRavvedimento = useCallback((id) => {
    setExpandedRavv((prev) => (prev === id ? null : id));
  }, []);

  const handleExportF24 = () => {
    if (!isPro) {
      setShowExportUpgrade(true);
      return;
    }
    exportF24List(selectedYear, f24ForYear);
  };

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear + 1; y >= currentYear - 3; y--) years.push(y);

  return (
    <div className={`min-h-screen transition-colors ${darkMode ? 'bg-neutral-950 text-white' : 'bg-neutral-50 text-neutral-900'}`}>
      <TopBar
        user={user}
        notificationsCount={0}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        onUploadLogo={() => {}}
        isSynced={isSynced}
      />

      {f24Locked ? (
        <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-24">
          <div className="mt-10 min-h-[50vh] flex items-center justify-center">
            <UpgradePrompt
              title="F24 e ravvedimento"
              message="Genera F24 e ravvedimento operoso con il piano Pro."
            />
          </div>
        </main>
      ) : (
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-24">
        {/* Header */}
        <div className="mt-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                I miei F24
              </h1>
              <p className={`mt-1 text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                Gestisci scadenze fiscali e pagamenti F24.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportF24}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-900 text-neutral-200 hover:bg-neutral-800'
                    : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                <FileDown size={16} />
                Esporta F24
              </button>
              <input
                ref={pdfInputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={handlePdfSelected}
                className="hidden"
              />
              <button
                onClick={handlePickPdf}
                disabled={importing}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-900 text-neutral-200 hover:bg-neutral-800'
                    : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                {importing ? <Loader2 size={16} className="animate-spin" /> : <FileUp size={16} />}
                {importing ? "Lettura PDF..." : "Importa da PDF"}
              </button>
              <button
                onClick={handleCreate}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-forfy-500 text-white text-sm font-medium hover:bg-forfy-600 transition-colors"
              >
                <Plus size={16} />
                Nuovo F24
              </button>
            </div>
          </div>

          {importError && (
            <div className={`mt-3 flex items-start gap-2 p-3 rounded-lg border text-sm ${
              darkMode ? 'bg-red-900/30 border-red-700 text-red-200' : 'bg-red-50 border-red-200 text-red-800'
            }`}>
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>{importError}</span>
            </div>
          )}
        </div>

        {/* Riepilogo */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className={`rounded-xl border p-4 ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-neutral-200'}`}>
            <p className={`text-xs uppercase tracking-wide ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Totale da pagare</p>
            <p className="text-xl font-bold mt-1">{formatEuro(riepilogo.totaleDaPagare)}</p>
          </div>
          <div className={`rounded-xl border p-4 ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-neutral-200'}`}>
            <p className={`text-xs uppercase tracking-wide ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Di cui scaduti</p>
            <p className="text-xl font-bold text-red-600 mt-1">{formatEuro(riepilogo.totaleScaduti)}</p>
            {riepilogo.totaleScaduti > 0 && (
              <p className="text-xs text-red-500 mt-0.5">
                Con ravvedimento: {formatEuro(riepilogo.totaleConRavvedimento)}
              </p>
            )}
          </div>
          <div className={`rounded-xl border p-4 ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-neutral-200'}`}>
            <p className={`text-xs uppercase tracking-wide ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Prossima scadenza</p>
            {riepilogo.prossimo ? (
              <>
                <p className="text-xl font-bold mt-1">{formatDateIT(riepilogo.prossimo.dataScadenza)}</p>
                <p className={`text-xs mt-0.5 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>{formatEuro(riepilogo.prossimo.importo)}</p>
              </>
            ) : (
              <p className={`text-sm mt-1 ${darkMode ? 'text-neutral-600' : 'text-neutral-400'}`}>Nessuna</p>
            )}
          </div>
        </div>

        {/* Filtri */}
        <div className={`rounded-xl border p-4 mb-6 ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-neutral-200'}`}>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className={`px-3 py-2 rounded-lg border text-sm ${
                darkMode ? 'border-neutral-700 bg-neutral-800 text-white' : 'border-neutral-200 bg-white text-neutral-900'
              }`}
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>

            <div className="flex gap-1">
              {FILTER_STATI.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setFiltroStato(s.value)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                    filtroStato === s.value
                      ? darkMode
                        ? "bg-forfy-500 text-white"
                        : "bg-forfy-50 text-forfy-700"
                      : darkMode
                        ? "text-neutral-400 hover:bg-neutral-800"
                        : "text-neutral-600 hover:bg-neutral-100"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Lista scaduti */}
        {filtered.scaduti.length > 0 && (
          <Section title="Scaduti" icon={<AlertTriangle size={16} />} color="red" darkMode={darkMode}>
            {filtered.scaduti.map((f) => {
              const ravv = calcolaRavvedimento(f.importo, f.dataScadenza, today);
              return (
                <F24Card
                  key={f.id}
                  f24={f}
                  status="scaduto"
                  darkMode={darkMode}
                  ravvedimento={ravv}
                  expandedRavv={expandedRavv === f.id}
                  onToggleRavv={() => toggleRavvedimento(f.id)}
                  onMarkPaid={() => handleMarkPaid(f)}
                  onEdit={() => handleEdit(f)}
                  onDuplicate={Number(f.anno) !== currentYear ? () => handleDuplicate(f) : undefined}
                  duplicateYear={currentYear}
                  onDelete={() => setConfirmDelete(f.id)}
                  paying={payingId === f.id}
                />
              );
            })}
          </Section>
        )}

        {/* Lista in scadenza */}
        {filtered.inScadenza.length > 0 && (
          <Section title="In scadenza" icon={<Clock size={16} />} color="yellow" darkMode={darkMode}>
            {filtered.inScadenza.map((f) => (
              <F24Card
                key={f.id}
                f24={f}
                status="in_scadenza"
                darkMode={darkMode}
                onMarkPaid={() => handleMarkPaid(f)}
                onEdit={() => handleEdit(f)}
                onDuplicate={Number(f.anno) !== currentYear ? () => handleDuplicate(f) : undefined}
                duplicateYear={currentYear}
                onDelete={() => setConfirmDelete(f.id)}
                paying={payingId === f.id}
              />
            ))}
          </Section>
        )}

        {/* Lista pagati */}
        {filtered.pagati.length > 0 && (
          <Section title="Pagati" icon={<CheckCircle size={16} />} color="green" darkMode={darkMode}>
            {filtered.pagati.map((f) => (
              <F24Card
                key={f.id}
                f24={f}
                status="pagato"
                darkMode={darkMode}
                onEdit={() => handleEdit(f)}
                onDuplicate={Number(f.anno) !== currentYear ? () => handleDuplicate(f) : undefined}
                duplicateYear={currentYear}
                onDelete={() => setConfirmDelete(f.id)}
              />
            ))}
          </Section>
        )}

        {/* Empty state */}
        {f24ForYear.length === 0 && (
          <div className={`rounded-xl border p-8 text-center ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-neutral-200'}`}>
            <p className={darkMode ? 'text-neutral-500' : 'text-neutral-500'}>Nessun F24 per il {selectedYear}</p>
            <button
              onClick={handleCreate}
              className="mt-3 text-forfy-500 hover:text-forfy-600 text-sm font-medium"
            >
              + Aggiungi il primo F24
            </button>
          </div>
        )}
      </main>
      )}

      {/* Modal */}
      <F24Modal
        open={modalOpen}
        mode={modalMode}
        initialValues={editingF24}
        year={selectedYear}
        codiceSedeInps={fiscalSettings.codiceSedeInps}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        darkMode={darkMode}
      />

      {/* Upgrade prompt per export (utente non Pro che prova a esportare) */}
      {showExportUpgrade && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowExportUpgrade(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <UpgradePrompt
              title="Export PDF disponibile con Pro"
              message="Esporta la lista F24 in PDF passando al piano Pro."
            />
          </div>
        </div>
      )}

      {/* Confirm delete */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className={`rounded-xl p-6 max-w-sm w-full shadow-xl ${darkMode ? 'bg-neutral-900 border border-neutral-800' : 'bg-white'}`}>
            <p className="font-medium mb-2">Eliminare questo F24?</p>
            <p className={`text-sm mb-4 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>L'operazione non è reversibile.</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className={`px-3 py-1.5 rounded-lg text-sm ${
                  darkMode ? "bg-neutral-800 hover:bg-neutral-700" : "bg-neutral-100 hover:bg-neutral-200"
                }`}
              >
                Annulla
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="px-3 py-1.5 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700"
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== Sub-components ==========

function Section({ title, icon, color, darkMode, children }) {
  const colors = {
    red: "text-red-600",
    yellow: darkMode ? "text-amber-400" : "text-amber-600",
    green: "text-emerald-600",
  };

  return (
    <div className="mb-6">
      <div className={`flex items-center gap-2 mb-3 ${colors[color] || ""}`}>
        {icon}
        <h2 className="text-sm font-semibold uppercase tracking-wide">{title}</h2>
        <span className="text-xs opacity-60">({React.Children.count(children)})</span>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function F24Card({
  f24,
  status,
  darkMode,
  ravvedimento,
  expandedRavv,
  onToggleRavv,
  onMarkPaid,
  onEdit,
  onDuplicate,
  duplicateYear,
  onDelete,
  paying,
}) {
  const cardClass = darkMode ? "bg-neutral-900 border-neutral-800" : "bg-white border-neutral-200";

  return (
    <div className={`${cardClass} rounded-xl border p-4`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate">{f24.descrizione || f24.tipo}</span>
            {f24.codiceTributo && (
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                darkMode ? "bg-neutral-800 text-neutral-400" : "bg-neutral-100 text-neutral-600"
              }`}>
                {f24.codiceTributo}
              </span>
            )}
            {status === "pagato" && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                PAGATO
              </span>
            )}
          </div>

          <div className={`flex items-center gap-3 mt-1 text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
            {status === "scaduto" && (
              <>
                <span>Scaduto il {formatDateIT(f24.dataScadenza)}</span>
                <span className="text-red-500 font-medium">
                  {Math.abs(getDaysUntil(f24.dataScadenza))} giorni di ritardo
                </span>
              </>
            )}
            {status === "in_scadenza" && (
              <>
                <span>Scadenza: {formatDateIT(f24.dataScadenza)}</span>
                <span className={`font-medium ${getDaysUntil(f24.dataScadenza) <= 7 ? "text-amber-600" : ""}`}>
                  Scade tra {getDaysUntil(f24.dataScadenza)} giorni
                </span>
              </>
            )}
            {status === "pagato" && (
              <span>Pagato il {formatDateIT(f24.dataPagamento)}</span>
            )}
          </div>

          {f24.note && (
            <p className={`text-xs mt-1 truncate ${darkMode ? 'text-neutral-600' : 'text-neutral-400'}`}>{f24.note}</p>
          )}
        </div>

        <div className="text-right shrink-0">
          <p className={`font-bold ${status === "scaduto" ? "text-red-600" : ""}`}>
            {formatEuro(f24.importo)}
          </p>
          {status === "scaduto" && ravvedimento && (
            <p className="text-xs text-red-500 mt-0.5">
              Tot. con ravv.: {formatEuro(ravvedimento.totale)}
            </p>
          )}
        </div>
      </div>

      {/* Ravvedimento detail */}
      {status === "scaduto" && ravvedimento && (
        <div className="mt-2">
          <button
            onClick={onToggleRavv}
            className="flex items-center gap-1 text-xs text-forfy-500 hover:text-forfy-600"
          >
            {expandedRavv ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            Dettaglio ravvedimento
          </button>
          {expandedRavv && (
            <div className={`mt-2 p-3 rounded-lg text-sm space-y-1 ${
              darkMode ? "bg-neutral-800/50" : "bg-neutral-50"
            }`}>
              <div className="flex justify-between">
                <span>Tributo originale:</span>
                <span>{formatEuro(ravvedimento.importoOriginale)}</span>
              </div>
              <div className="flex justify-between">
                <span>Sanzione ({(ravvedimento.percentualeSanzione * 100).toFixed(2)}%):</span>
                <span>{formatEuro(ravvedimento.sanzione)}</span>
              </div>
              <div className="flex justify-between">
                <span>Interessi ({ravvedimento.giorniRitardo} giorni):</span>
                <span>{formatEuro(ravvedimento.interessi)}</span>
              </div>
              <div className={`flex justify-between font-bold border-t pt-1 mt-1 ${darkMode ? 'border-neutral-700' : 'border-neutral-300'}`}>
                <span>TOTALE DA PAGARE:</span>
                <span>{formatEuro(ravvedimento.totale)}</span>
              </div>
              <p className={`text-xs mt-1 ${darkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>{ravvedimento.fascia}</p>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mt-3">
        {status !== "pagato" && (
          <button
            onClick={onMarkPaid}
            disabled={paying}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            <CheckCircle size={14} />
            {paying ? "..." : "Segna pagato"}
          </button>
        )}
        <button
          onClick={onEdit}
          className={`p-1.5 rounded-lg transition-colors ${
            darkMode ? "hover:bg-neutral-800 text-neutral-400" : "hover:bg-neutral-100 text-neutral-500"
          }`}
          aria-label="Modifica"
        >
          <Edit2 size={14} />
        </button>
        {onDuplicate && (
          <button
            onClick={onDuplicate}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              darkMode ? "bg-neutral-800 text-neutral-300 hover:bg-neutral-700" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
            }`}
            title={`Duplica per ${duplicateYear}`}
          >
            <Copy size={14} />
            Duplica per {duplicateYear}
          </button>
        )}
        <button
          onClick={onDelete}
          className={`p-1.5 rounded-lg transition-colors ${
            darkMode ? "hover:bg-neutral-800 text-neutral-400" : "hover:bg-neutral-100 text-neutral-500"
          }`}
          aria-label="Elimina"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}
